/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react"
import { AxiosError } from "axios"

import { Notification } from "@/components"

import Messages from "@/utils/responseMessages/messages"
import { getErrorName, getStatusCode } from "@/utils/response"

export type ErrorType = AxiosError & {
	message: string
	/**
	 * Http Response.
	 */
	response: {
		status: string
		data: {
			error: string
			codeMessages: Record<string, string>
		}
	}
	/**
	 * Socket Response.
	 */
	body: {
		codeMessages: Record<string, string>
		errorCode?: string
	}
}

export type ValidationType = {
	[key: string]: any
}

/**
	*
	* @param {error} AxiosError
	*/
export const parseError = (error: ErrorType) => {
	const httpCodeMessages = error?.response?.data?.codeMessages
	const socketCodeMessages = error?.body?.codeMessages

	const validationMessages = httpCodeMessages || socketCodeMessages
	const errorMessage = error?.message
	const errorName = getErrorName(error)
	const errorStatus = getStatusCode(error)

	return {
		validationMessages,
		errorMessage,
		errorName,
		errorStatus
	}
}

const useValidation = () => {
	const [validation, setValidation] = useState<ValidationType>({})

	// eslint-disable-next-line
	const triggerUndeterminedValidation = (error: ErrorType) => {
		Notification.error({ message: "Algo deu errado e não conseguimos identificar. Entre em contato." })
		if (process.env.NODE_ENV === "development") {
			// eslint-disable-next-line

		}
	}

	// Errors status in this array are treated in "src/services/Api.ts"
	// 403: forbiden status
	const alreadyHandledErrorStatus = [403]

	/**
		*
		* @param {error} AxiosError
		*/
	const triggerValidation = (error: ErrorType) => {
		const data = parseError(error)

		if (!alreadyHandledErrorStatus.includes(Number(data.errorStatus))) {
			if (!data.validationMessages) {
				triggerUndeterminedValidation(error)
			} else {
				const validationMessages = data.validationMessages

				Object.keys(validationMessages)
					.forEach(function (key: any) {
						validationMessages[key] = Messages(validationMessages[key] as string)
					})

				setValidation((lastValidation) => ({
					...lastValidation,
					...validationMessages
				}))
			}
		}
	}

	const addValidation = (newValidation: ValidationType) => {
		setValidation((lastValidation) => ({
			...lastValidation,
			...newValidation
		}))
	}

	const clearValidation = (key: string) => {
		if (key in validation) {
			setValidation((lastValidation) => {
				const updatedData: any = { ...lastValidation }

				delete updatedData[key]

				return updatedData
			})
		}
	}

	const clearAllValidations = () => {
		setValidation({})
	}

	return {
		triggerUndeterminedValidation,
		triggerValidation,
		addValidation,
		clearValidation,
		clearAllValidations,
		parseError,
		validation
	}
}

export default useValidation
